import * as React from 'react'
import { styled } from '@mui/material/styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import MuiAccordion from '@mui/material/Accordion'
import MuiAccordionSummary from '@mui/material/AccordionSummary'
import MuiAccordionDetails from '@mui/material/AccordionDetails'
import Typography from '@mui/material/Typography'
import { Box, Chip } from '@mui/material'
import { faArrowDownToLine, faFile, faGraduationCap, faCertificate, faBriefcase, faEnvelope, faFolder } from '@fortawesome/pro-solid-svg-icons'
import lightTheme from '../../styles/theme/lightTheme'
import GedTable from './GedTable'
import { useTranslation } from 'react-i18next'

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid rgba(229, 231, 235, 0.8)`,
  borderRadius: '12px',
  marginBottom: '8px',
  overflow: 'hidden',
  '&:not(:last-child)': {
    borderBottom: `1px solid rgba(229, 231, 235, 0.8)`,
  },
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: '0 0 8px 0',
    '&:first-of-type': {
      marginTop: 0,
    },
  },
}))

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: 'rgba(249, 250, 251, 0.8)',
  minHeight: '56px',
  flexDirection: 'row-reverse',
  borderRadius: '12px',
  '& .MuiAccordionSummary-expandIconWrapper': {
    color: '#667eea',
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
    margin: '12px 0',
  },
  '&:hover': {
    backgroundColor: 'rgba(102, 126, 234, 0.05)',
  },
}))

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: '0',
}))

export default function GedTabs({ files, dateFile, handleRemove }) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = React.useState('')

  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false)
  }

  const getFileCount = (types) => {
    if (Array.isArray(types)) {
      return files.filter((obj) => types.includes(obj.type)).length
    }
    return files.filter((obj) => obj.type === types).length
  }

  const getDocumentIcon = (type) => {
    switch (type) {
      case 'cv':
      case 'Cv':
        return faFile
      case 'Diplômes':
        return faGraduationCap
      case 'Certifications Techniques':
        return faCertificate
      case 'Certificat de travail':
        return faBriefcase
      case 'Lettre de motivation':
        return faEnvelope
      default:
        return faFolder
    }
  }

  return (
    <div>
      {/* CV Section */}
      <Accordion
        expanded={expanded === 'panel1'}
        onChange={handleChange('panel1')}
      >
        <AccordionSummary aria-controls="panel1d-content" id="panel1d-header">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <FontAwesomeIcon 
              icon={faFile} 
              color={getFileCount(['Cv', 'cv']) === 0 ? '#ef4444' : '#667eea'}
              fontSize={18}
            />
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 500,
                color: getFileCount(['Cv', 'cv']) === 0 ? '#ef4444' : '#1f2937',
                fontSize: { xs: '14px', sm: '16px' }
              }}
            >
              {t('list.documentType1')}
            </Typography>
            <Chip
              label={getFileCount(['Cv', 'cv'])}
              size="small"
              sx={{
                backgroundColor: getFileCount(['Cv', 'cv']) === 0 ? '#fef2f2' : 'rgba(102, 126, 234, 0.1)',
                color: getFileCount(['Cv', 'cv']) === 0 ? '#ef4444' : '#667eea',
                fontWeight: 600,
                fontSize: '12px',
                minWidth: '24px',
                height: '24px',
              }}
            />
            {getFileCount(['Cv', 'cv']) === 0 && (
              <Chip
                label="Required"
                size="small"
                sx={{
                  backgroundColor: '#fef2f2',
                  color: '#ef4444',
                  fontWeight: 600,
                  fontSize: '11px',
                  height: '20px',
                }}
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {getFileCount(['Cv', 'cv']) > 0 ? (
            <GedTable
              files={files.filter((obj) => obj.type === 'Cv' || obj.type === 'cv')}
              dateFile={dateFile}
              handleRemove={handleRemove}
            />
          ) : (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                {t('list.noFile')}
              </Typography>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Diplomas Section */}
      <Accordion
        expanded={expanded === 'panel2'}
        onChange={handleChange('panel2')}
      >
        <AccordionSummary aria-controls="panel2d-content" id="panel2d-header">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <FontAwesomeIcon 
              icon={faGraduationCap} 
              color="#667eea"
              fontSize={18}
            />
            <Typography variant="h6" sx={{ fontWeight: 500, color: '#1f2937', fontSize: { xs: '14px', sm: '16px' } }}>
              {t('list.documentType2')}
            </Typography>
            <Chip
              label={getFileCount('Diplômes')}
              size="small"
              sx={{
                backgroundColor: 'rgba(102, 126, 234, 0.1)',
                color: '#667eea',
                fontWeight: 600,
                fontSize: '12px',
                minWidth: '24px',
                height: '24px',
              }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {getFileCount('Diplômes') > 0 ? (
            <GedTable
              files={files.filter((obj) => obj.type === 'Diplômes')}
              dateFile={dateFile}
              handleRemove={handleRemove}
            />
          ) : (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                {t('list.noFile')}
              </Typography>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Technical Certifications Section */}
      <Accordion
        expanded={expanded === 'panel3'}
        onChange={handleChange('panel3')}
      >
        <AccordionSummary aria-controls="panel3d-content" id="panel3d-header">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <FontAwesomeIcon 
              icon={faCertificate} 
              color="#667eea"
              fontSize={18}
            />
            <Typography variant="h6" sx={{ fontWeight: 500, color: '#1f2937', fontSize: { xs: '14px', sm: '16px' } }}>
              {t('list.documentType3')}
            </Typography>
            <Chip
              label={getFileCount('Certifications Techniques')}
              size="small"
              sx={{
                backgroundColor: 'rgba(102, 126, 234, 0.1)',
                color: '#667eea',
                fontWeight: 600,
                fontSize: '12px',
                minWidth: '24px',
                height: '24px',
              }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {getFileCount('Certifications Techniques') > 0 ? (
            <GedTable
              files={files.filter((obj) => obj.type === 'Certifications Techniques')}
              dateFile={dateFile}
              handleRemove={handleRemove}
            />
          ) : (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                {t('list.noFile')}
              </Typography>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Work Certificates Section */}
      <Accordion
        expanded={expanded === 'panel4'}
        onChange={handleChange('panel4')}
      >
        <AccordionSummary aria-controls="panel4d-content" id="panel4d-header">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <FontAwesomeIcon 
              icon={faBriefcase} 
              color="#667eea"
              fontSize={18}
            />
            <Typography variant="h6" sx={{ fontWeight: 500, color: '#1f2937', fontSize: { xs: '14px', sm: '16px' } }}>
              {t('list.documentType5')}
            </Typography>
            <Chip
              label={getFileCount('Certificat de travail')}
              size="small"
              sx={{
                backgroundColor: 'rgba(102, 126, 234, 0.1)',
                color: '#667eea',
                fontWeight: 600,
                fontSize: '12px',
                minWidth: '24px',
                height: '24px',
              }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {getFileCount('Certificat de travail') > 0 ? (
            <GedTable
              files={files.filter((obj) => obj.type === 'Certificat de travail')}
              dateFile={dateFile}
              handleRemove={handleRemove}
            />
          ) : (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                {t('list.noFile')}
              </Typography>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Cover Letter Section */}
      <Accordion
        expanded={expanded === 'panel6'}
        onChange={handleChange('panel6')}
      >
        <AccordionSummary aria-controls="panel6d-content" id="panel6d-header">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <FontAwesomeIcon 
              icon={faEnvelope} 
              color="#667eea"
              fontSize={18}
            />
            <Typography variant="h6" sx={{ fontWeight: 500, color: '#1f2937', fontSize: { xs: '14px', sm: '16px' } }}>
              {t('list.documentType4')}
            </Typography>
            <Chip
              label={getFileCount('Lettre de motivation')}
              size="small"
              sx={{
                backgroundColor: 'rgba(102, 126, 234, 0.1)',
                color: '#667eea',
                fontWeight: 600,
                fontSize: '12px',
                minWidth: '24px',
                height: '24px',
              }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {getFileCount('Lettre de motivation') > 0 ? (
            <GedTable
              files={files.filter((obj) => obj.type === 'Lettre de motivation')}
              dateFile={dateFile}
              handleRemove={handleRemove}
            />
          ) : (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                {t('list.noFile')}
              </Typography>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Other Documents Section */}
      <Accordion
        expanded={expanded === 'panel5'}
        onChange={handleChange('panel5')}
      >
        <AccordionSummary aria-controls="panel5d-content" id="panel5d-header">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <FontAwesomeIcon 
              icon={faFolder} 
              color="#667eea"
              fontSize={18}
            />
            <Typography variant="h6" sx={{ fontWeight: 500, color: '#1f2937', fontSize: { xs: '12px', sm: '14px' } }}>
              {t('list.documentType8')}
            </Typography>
            <Chip
              label={getFileCount(['Recommandations', 'References', 'document', 'Autres'])}
              size="small"
              sx={{
                backgroundColor: 'rgba(102, 126, 234, 0.1)',
                color: '#667eea',
                fontWeight: 600,
                fontSize: '12px',
                minWidth: '24px',
                height: '24px',
              }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {getFileCount(['Recommandations', 'References', 'document', 'Autres']) > 0 ? (
            <GedTable
              files={files.filter(
                (obj) =>
                  obj.type === 'Recommandations' ||
                  obj.type === 'References' ||
                  obj.type === 'document' ||
                  obj.type === 'Autres'
              )}
              dateFile={dateFile}
              handleRemove={handleRemove}
            />
          ) : (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                {t('list.noFile')}
              </Typography>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </div>
  )
}
